import { Button, Card } from "@tremor/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acceptWorkspaceInvitation, fetchWorkspaceInvitationDetails } from "../api/workspaces.js";
import { fetchMyWorkspaces } from "../api/me.js";
import { useAuthStore } from "../store/authStore.js";
import { API_BASE_URL } from "../consts.js";
import { useTranslation } from "react-i18next";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}

export default function Invite() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { token } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const isLoggedIn = useAuthStore((s) => !!s.accessToken);
  const setWorkspaces = useAuthStore((s) => s.setWorkspaces);

  useEffect(() => {
    async function loadInvitation() {
      try {
        const data = await fetchWorkspaceInvitationDetails(token);
        setInvitation(data);
      } catch (err) {
        setError(err.response?.data?.error ?? t("invite.error_title"));
      } finally {
        setLoading(false);
      }
    }

    loadInvitation();
  }, [token, t]);

  async function handleAccept() {
    setAccepting(true);
    setError(null);
    try {
      await acceptWorkspaceInvitation(token);
      const orgs = await fetchMyWorkspaces();
      setWorkspaces(orgs);
      navigate("/pick-workspace", { replace: true });
    } catch (err) {
      setError(err.response?.data?.error ?? t("invite.error_title"));
      setAccepting(false);
    }
  }

  function goToLogin() {
    navigate(`/login?workspace_invitation_token=${encodeURIComponent(token)}`);
  }

  function goToSignup() {
    const params = new URLSearchParams({
      workspace_invitation_token: token,
      email: invitation?.email ?? "",
    });
    navigate(`/signup?${params.toString()}`);
  }

  function goToGoogleAuth() {
    const url = `${API_BASE_URL}/auth/google/start?workspace_invitation_token=${encodeURIComponent(token)}`;
    window.location.assign(url);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-lg">
        {loading ? (
          <p className="text-sm text-gray-400">{t("invite.loading")}</p>
        ) : error ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900">{t("invite.error_title")}</h1>
            <p className="mt-3 text-sm text-red-600">{error}</p>
            <Button className="mt-6" onClick={() => navigate("/login")}>
              {t("invite.go_to_login")}
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm font-semibold uppercase tracking-wide text-teal-600">
              {t("invite.you_are_invited")}
            </p>
            <h1 className="mt-2 text-2xl font-bold text-gray-900">
              {t("invite.join_title", { workspace: invitation.workspace_name })}
            </h1>
            <p className="mt-3 text-sm text-gray-600">
              {t("invite.description", { inviter: invitation.invited_by_email, email: invitation.email, workspace: invitation.workspace_name, role: invitation.role })}
            </p>

            {isLoggedIn ? (
              <Button className="mt-6" loading={accepting} onClick={handleAccept}>
                {t("invite.accept")}
              </Button>
            ) : (
              <>
                <div className="mt-6 flex gap-3">
                  <Button onClick={goToSignup}>{t("invite.create_account")}</Button>
                  <Button variant="secondary" onClick={goToLogin}>{t("invite.sign_in")}</Button>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <hr className="flex-1 border-gray-200" />
                  <span className="text-xs text-gray-400">{t("invite.or")}</span>
                  <hr className="flex-1 border-gray-200" />
                </div>

                <button
                  type="button"
                  onClick={goToGoogleAuth}
                  className="mt-4 flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                >
                  <GoogleIcon />
                  {t("invite.continue_with_google")}
                </button>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
